// src/components/error/AppErrorBoundary.tsx
import { ErrorBoundary } from "@sentry/react";
import type { ReactNode } from "react";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

const CHUNK_RELOAD_FLAG = "chunk-error-reload";

/** Tras un deploy, los chunks lazy viejos dejan de existir (404). "Reintentar" no lo arregla: hay que recargar. */
function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
    msg
  );
}

/**
 * Recarga "a fondo": si el Service Worker quedó viejo (sirviendo un index.html
 * que apunta a chunks que ya no existen en el server), un reload común puede
 * seguir sirviéndose desde ESE MISMO caché viejo y repetir el error. Por eso
 * acá desregistramos el SW y borramos el Cache Storage antes de recargar —
 * así el próximo fetch va sí o sí a la red.
 */
async function hardRecover() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // si falla la limpieza, igual intentamos recargar
  } finally {
    window.location.reload();
  }
}

/**
 * Error Boundary principal de la aplicación
 * Captura errores no manejados y muestra UI de fallback
 */
export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error("Error capturado por ErrorBoundary:", error, errorInfo);
        if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
          // Recarga a fondo una única vez (evita loop si el problema persiste por otro motivo)
          sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
          void hardRecover();
        }
      }}
      fallback={({ error, resetError }) => (
        <div className="min-h-screen bg-pastel-cream flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* Icono de error */}
            <div className="w-16 h-16 mx-auto mb-4 bg-rose-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Título */}
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Algo salió mal</h1>

            {/* Mensaje */}
            <p className="text-slate-600 mb-6">
              Lo sentimos, ocurrió un error inesperado. Nuestro equipo ha sido notificado y estamos trabajando para
              solucionarlo.
            </p>

            {/* Detalles del error (solo en desarrollo) */}
            {!import.meta.env.PROD && (
              <details className="mb-6 text-left">
                <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-700 mb-2">
                  Detalles técnicos
                </summary>
                <pre className="text-xs bg-slate-100 p-4 rounded-lg overflow-auto max-h-48 text-rose-700">
                  {String(error)}
                  {error instanceof Error && error.stack && `\n\n${error.stack}`}
                </pre>
              </details>
            )}

            {/* Acciones */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={resetError}
                className="px-6 py-2 bg-pastel-pink text-slate-900 rounded-lg hover:bg-pastel-pink/80 transition-colors font-medium"
              >
                Intentar de nuevo
              </button>
              <button
                onClick={() => void hardRecover()}
                className="px-6 py-2 bg-slate-200 text-slate-900 rounded-lg hover:bg-slate-300 transition-colors font-medium"
              >
                Volver al inicio
              </button>
            </div>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
